// Sticky "slide-over" section stacking.
// For each section flagged [data-sticky-stack], wrap it and the section above it
// in a .sticky-stack-group. The section above is pinned (sticky) and the flagged
// one slides up over it. Wrapping limits the pin to the pair, so the pinned
// section releases as the slide-over finishes instead of bleeding over later
// sections. Pure CSS scroll — no JS animation.
//
// Pin offset: the pinned section is pinned at its BOTTOM when it is taller than
// the viewport (top = min(0, viewportHeight - sectionHeight)). This lets the
// whole section scroll into view before it pins and the next section starts
// sliding over it. For sections shorter than the viewport this is top:0, i.e.
// unchanged (the mobile case).
//
// White gap: because the pinned section covers the whole viewport when it is
// taller than it, the only white that can show before the slide is white that
// is part of the pinned section's own box. The flagged section may request an
// extra white spacer appended to the bottom of the pinned section via
// [data-sticky-stack-gap="<px>"]. It is applied only on desktop and only when
// the pinned section is taller than the viewport — so the mobile experience is
// untouched.
(function () {
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESKTOP = window.matchMedia('(min-width: 750px)');

  // Track wrapped pairs so we can recompute the pin offset / gap on resize or
  // image load, when the pinned section's height changes.
  var groups = [];

  // Inject a stylesheet (once) whose rules use !important so they beat the
  // theme's `[role=main] .shopify-section { position: relative }`. !important
  // always wins over a non-important declaration regardless of specificity,
  // and a stylesheet rule can't be wiped the way an inline style can.
  // NOTE: `top` is intentionally NOT set here — it is set inline per group by
  // positionPrev() (inline !important beats this stylesheet's !important).
  function ensureStyle() {
    if (document.getElementById('sticky-stack-style')) return;
    var s = document.createElement('style');
    s.id = 'sticky-stack-style';
    s.textContent =
      // Clip the group so the pinned hero (and its overflowing image / the theme's
      // rounded-corner ::before) can't bleed below the group into the next
      // section's transparent areas. `clip` does not create a scroll container, so
      // it does not break the sticky pin.
      '.sticky-stack-group{overflow:clip!important}' +
      '.sticky-stack-group .sticky-stack-prev{position:sticky!important;z-index:0!important}' +
      // High z-index so the collage's opaque background decisively covers the hero
      // and the hero's own z-index:1 overlay content.
      '.sticky-stack-group .sticky-stack-over{position:relative!important;z-index:2!important}' +
      // Square the sliding section's BOTTOM corners. The theme rounds the bottom
      // (~border-radius), and with overflow:clip those rounded corners are
      // transparent — the hero behind shows through the gaps. Keep the TOP corners
      // rounded (intended look as it slides over).
      '.sticky-stack-group .sticky-stack-over{border-end-start-radius:0!important;border-end-end-radius:0!important}';
    document.head.appendChild(s);
  }

  // Size the white gap spacer, then pin the previous section at its bottom when
  // it is taller than the viewport (otherwise at the top). Set `top` inline with
  // !important so it wins the cascade.
  //
  // Natural height is measured as (offsetHeight - spacer height) so the spacer
  // never feeds back into the "taller than viewport" test — and we only write
  // the spacer height when it actually changes, so observing `prev` for resizes
  // can't loop.
  function positionPrev(g) {
    var prev = g.prev;
    var natural = prev.offsetHeight - g.spacer.offsetHeight;
    var wantGap = natural > window.innerHeight && DESKTOP.matches ? g.gap : 0;

    if (g.spacer.offsetHeight !== wantGap) {
      g.spacer.style.height = wantGap + 'px';
    }

    var top = Math.min(0, window.innerHeight - (natural + wantGap));
    prev.style.setProperty('top', top + 'px', 'important');
  }

  function repositionAll() {
    groups.forEach(positionPrev);
  }

  function wrapOne(flag) {
    var current = flag.closest('.shopify-section');
    if (!current) return;

    // Already wrapped.
    if (current.parentNode && current.parentNode.classList.contains('sticky-stack-group')) return;

    var prev = current.previousElementSibling;
    if (!prev || !prev.classList.contains('shopify-section')) return;

    var group = document.createElement('div');
    group.className = 'sticky-stack-group';
    prev.parentNode.insertBefore(group, prev);
    group.appendChild(prev);
    group.appendChild(current);

    ensureStyle();
    prev.classList.add('sticky-stack-prev');
    current.classList.add('sticky-stack-over');

    // Transparent white-gap spacer at the very bottom of the pinned section, so
    // it reads as white below the section's content before the slide begins.
    var spacer = document.createElement('div');
    spacer.className = 'sticky-stack-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.cssText = 'width:100%;pointer-events:none;';
    prev.appendChild(spacer);

    // The theme's `body { overflow-x: hidden }` silently disables position:
    // sticky on descendants. `clip` prevents horizontal scroll WITHOUT creating
    // a scroll container, so the pin works. !important inline = guaranteed win.
    document.body.style.setProperty('overflow-x', 'clip', 'important');

    var g = {
      group: group,
      prev: prev,
      over: current,
      spacer: spacer,
      gap: parseInt(flag.getAttribute('data-sticky-stack-gap'), 10) || 0
    };
    groups.push(g);
    positionPrev(g);

    // Recompute once the pinned section's images have settled its height.
    // Safe to observe `prev`: positionPrev only writes the spacer height when it
    // changes, and natural height excludes the spacer, so this can't feed back.
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { positionPrev(g); });
      ro.observe(prev);
    }
  }

  function unwrapAll() {
    document.querySelectorAll('.sticky-stack-spacer').forEach(function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
    document.querySelectorAll('.sticky-stack-group').forEach(function (group) {
      var parent = group.parentNode;
      while (group.firstChild) parent.insertBefore(group.firstChild, group);
      parent.removeChild(group);
    });
    document.querySelectorAll('.sticky-stack-prev').forEach(function (n) {
      n.classList.remove('sticky-stack-prev');
      n.style.removeProperty('top');
    });
    document.querySelectorAll('.sticky-stack-over').forEach(function (n) {
      n.classList.remove('sticky-stack-over');
    });
    document.body.style.removeProperty('overflow-x');
    groups = [];
  }

  function apply() {
    if (REDUCE.matches) return;
    document.querySelectorAll('[data-sticky-stack]').forEach(wrapOne);
  }

  function reinit() {
    unwrapAll();
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }

  // Recompute the pin offset / gap when the viewport or content height changes.
  window.addEventListener('resize', repositionAll);
  window.addEventListener('load', repositionAll);

  // Theme editor: sections get re-rendered/reordered, so rebuild the wrappers.
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (ev) {
    document.addEventListener(ev, reinit);
  });
})();
